import { createCrudActions, createSubResourceCrudActions } from './crud-actions'

/**
 * Convenience wrapper for CRUD actions.
 *
 * The first use case are simple resources. Assume that REST resource /foos supports the standard set of CRUD actions:
 *
 *   GET /api/foos
 *   POST /api/foos
 *   PUT /api/foos/123
 *   DELETE /api/foos/123
 *   PATCH /api/foos/123
 *
 * The actions for those REST requests can be created as follows:
 *
 *   const fooActions = createBoundActions('foos', '/api', '/foos', 'fooId')
 *
 * where 'fooId' is the name of the primary key of the base resource.
 * The actions can applied as follows:
 *
 *   fooActions.fetch()
 *   fooActions.insert({ fooName: 'Fred', ... })
 *   fooActions.update({ fooId: 123, fooName: 'Lars', ... })
 *   fooActions.remove({ fooId: 123, ... })
 *   fooActions.partialUpdate({ fooId: 123, ... })
 *
 * The second use case are operations on subresources. For example, REST resource /foos/123 may have a 'bar'
 * subresource, which supports the standard set of CRUD actions:
 *
 *   GET /api/foos/123/bars
 *   POST /api/foos/123/bars
 *   PUT /api/foos/123/bars/456
 *   DELETE /api/foos/123/bars/456
 *   PATCH /api/foos/123/bars/456
 *
 * Given a base object fooObject = { fooId: 123, ... }, the actions for those REST requests can be created as follows:
 *
 *   const barActions = fooActions.createSubResourceActions(fooObject, 'bars', '/bars', 'barId')
 *
 * where 'barId' is the primary key of the subresource.
 * The resulting sub-resource actions can then be applied exactly as the top-level actions:
 *
 *   barActions.fetch()
 *   barActions.insert({ barName: 'Hans', ... })
 *   barActions.update({ barId: 456, barName: 'Inge', ... })
 *   barActions.remove({ barId: 456, ... })
 *   barActions.partialUpdate({ barId: 456, barName: 'Inge', ... })
 *
 * Note that the 'insert' action also covers 'action verbs' according to §6.2.3 of the REST Design Principles.
 * For example, given a userObject, request 'POST /api/users/123/changePassword' can be created via
 *
 *   createBoundActions('users', '/api', '/users', 'userId')
 *     .createSubResourceActions(userObject, 'password', '/changePassword')
 *     .insert({ password: '***' })
 *
 * where 'password' is the name of the action to appear in the Redux log.
 * There may or may not be a corresponding Redux store.
 *
 * @param {string} resourceName - a name for identifying the REST resource (usually corresponds to a Redux store name)
 * @param {string} contextPath - the path prefix of the API
 *                 (e.g. if the API exposes a gadgets collection at /api/gadgets then '/api' is the context path)
 * @param {string} resourcePath - the relative path of the resource below the context path
 *                 (e.g. if the API exposes a gadgets collection at /api/gadgets then '/gadgets' is the resource path)
 * @param {string} [key] the attribute name under which the unique resource id can be found.
 *                 You may provide null if there is no key.
 * @returns {object} an object of CRUD actions bound to the given resource path
 */
export function createBoundActions(resourceName, contextPath, resourcePath, key) {
    if (arguments.length < 3 || arguments.length > 4) {
        throw new Error('Function expects 3 or 4 arguments')
    }
    if (!resourcePath || !resourcePath.startsWith('/')) {
        throw new Error('The resource path must start with a slash')
    }

    // Create the CRUD actions for the actual request handling
    const actions = createCrudActions(resourceName, contextPath)

    return createActions(actions, resourcePath, createPathWithBaseKey, getBaseKey)

    // Extracts the value of the primary key of the passed object
    function getKeyValue(json, key) {
        if (!key) {
            throw new Error('A key is required for update and remove operations')
        }
        if (!json[key]) {
            throw new Error(`Missing key '${key}' in resource ${JSON.stringify(json)}`)
        }
        return json[key]
    }

    // Returns the base path including the key of the base resource, e.g. '/foos/123'
    function createPathWithKey(resourcePath, json, key) {
        return key !== null ? resourcePath + '/' + getKeyValue(json, key) : resourcePath
    }

    // Returns the base path including the givin' id, e.g. '/foos/123'
    function createPathWithId(resourcePath, id) {
        return id ? resourcePath + '/' + id : resourcePath
    }

    function createPathWithBaseKey(json) {
        return createPathWithKey(resourcePath, json, key)
    }

    function getBaseKey(json) {
        return getKeyValue(json, key)
    }

    function createActions(actions, basePath, createResourcePath, getBaseKey) {
        return {
            fetch(options) {
                return actions.fetch(basePath, options)
            },
            fetchOne(id, options) {
                return actions.fetch(createPathWithId(basePath, id), options)
            },
            diff(modified, params, options) {
                return actions.diff(basePath, modified, params, options)
            },
            insert(json, params, options) {
                return actions.insert(basePath, json, params, options)
            },
            update(json, params, options) {
                return actions.update(createResourcePath(json), json, params, options)
            },
            partialUpdate(json, params, options) {
                return actions.partialUpdate(createResourcePath(json), json, params, options)
            },
            /**
             * @deprecated since version 18.0.0, can be removed starting with version 19.
             *
             * in some places the old settings endpoint, which does not follow the rest-design-principles
             * is still used via this method. To give some time for migration, we should only remove this
             * starting with version 19.
             */
            updateArray(json, params) {
                console.warn(
                    '`updateArray` is deprecated, please use the proper ' +
                        'action created with createBulkResourceActions instead.'
                )
                return actions.update(basePath, json, params)
            },
            remove(json, params, options) {
                return actions.remove(createResourcePath(json), json, params, options)
            },
            assign(json, params) {
                return actions.assign(basePath, json, params)
            },
            unassign(json, params) {
                return actions.unassign(basePath, json, params)
            },

            createBulkResourceActions(bulkResourcePath) {
                const fullPath = basePath + '/' + bulkResourcePath

                return {
                    fetch(options) {
                        return actions.fetch(fullPath, options)
                    },
                    insert(json, params, options) {
                        return actions.insert(fullPath, json, params, options)
                    },
                    update(json, params) {
                        return actions.insert(fullPath, json, params)
                    },
                    remove(json, params) {
                        return actions.removeArray(fullPath, json, params)
                    },
                    partialUpdate(json, params, options) {
                        return actions.partialUpdate(fullPath, json, params, options)
                    }
                }
            },

            createActionVerbAction(actionVerbName, actionVerbPath) {
                if (!actionVerbPath || !actionVerbPath.startsWith('/')) {
                    throw new Error('The resource path must start with a slash')
                }

                return (parentJson, json, params, options) => {
                    // Extends the resource path by the relative path of the action verb, e.g. '/foos/123/accelerate'
                    const pathWithActionVerb = createResourcePath(parentJson) + actionVerbPath

                    // Create the CRUD actions for the action verb - subresource
                    return createSubResourceCrudActions(actionVerbName, getBaseKey(parentJson), contextPath).insert(
                        pathWithActionVerb,
                        json,
                        params,
                        options
                    )
                }
            },

            createSubResourceActions(json, subResourceName, subResourcePath, subKey) {
                if (!subResourcePath || !subResourcePath.startsWith('/')) {
                    throw new Error('The resource path must start with a slash')
                }

                // Extends the resource path by the relative path of the sub-resource, e.g. '/foos/123/bars'
                const pathWithSubResource = createResourcePath(json) + subResourcePath

                // Returns the path including the key of the subresource, e.g. '/foos/123/bars/456'
                function createPathWithSubKey(json) {
                    return createPathWithKey(pathWithSubResource, json, subKey)
                }

                // Create the CRUD actions for the handling sub-resource requests
                const subActions = createSubResourceCrudActions(subResourceName, getBaseKey(json), contextPath)
                const getSubResourceKey = json => subKey && getKeyValue(json, subKey)

                return createActions(subActions, pathWithSubResource, createPathWithSubKey, getSubResourceKey)
            }
        }
    }
}
